import { useContext } from "react";
import { View, XStack, YStack } from "tamagui";

import AIDoctorIntakeFlow from "@/ai-doctor/AIDoctorIntakeFlow";
import { WorkflowType } from "@/api/generated/models";
import GlpHeroImage from "@/assets/images/landing_page/v3/medical-weight-accordion-01.png";
import { Button, Image, Section, Square, Text } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { Link, Paragraph } from "@/components/Typography";
import type { Glp1PartnerDisplay } from "./Glp1LandingPage";

export default function Glp1Hero({
  onPress,
  isPending,
  partnerDisplay,
  showPartnerOffer = false,
}: {
  onPress: () => void;
  isPending: boolean;
  partnerDisplay: Glp1PartnerDisplay;
  showPartnerOffer?: boolean;
}) {
  const { lg, setModalContent } = useContext(LayoutContext);

  const textContent = (
    <YStack flex={lg ? 1 : undefined} justifyContent="center">
      <View
        backgroundColor={partnerDisplay.brandColor}
        borderRadius={999}
        paddingHorizontal="$300"
        paddingVertical="$100"
        alignSelf={lg ? "flex-start" : "center"}
        marginBottom="$300"
      >
        <Text size="$sm" color="$neutral0">
          Welcome{" "}
          <Text size="$sm" semibold color="$neutral0">
            {partnerDisplay.name}
          </Text>{" "}
          {partnerDisplay.welcomeChipSuffix ?? "customers"}
        </Text>
      </View>

      <Text size="$xxl" semibold $lg={{ size: "$3xl", semibold: true }}>
        GLP-1s for weight loss from licensed clinicians
      </Text>

      <YStack marginTop="$300" marginBottom="$400" gap="$200">
        <Text size="$lg" color="$textSecondary">
          {partnerDisplay.heroSubtitle ??
            "Curai Health offers GLP-1 prescriptions to support your weight loss goals."}
        </Text>
        {showPartnerOffer && (
          <Text size="$md" color="$textSecondary">
            {partnerDisplay.heroOfferCopy}
          </Text>
        )}
        {partnerDisplay.heroAltCareLink && (
          <Paragraph size="$lg">
            {partnerDisplay.heroAltCareLink.prompt}{" "}
            <Link size="$lg" href={partnerDisplay.heroAltCareLink.href} bold>
              {partnerDisplay.heroAltCareLink.linkCopy}
            </Link>
          </Paragraph>
        )}
      </YStack>

      <Section alignItems="center" gap="$200">
        <Button onPress={onPress} disabled={isPending} alignSelf="stretch">
          See if you are eligible →
        </Button>
        <Button
          mode="secondary"
          onPress={() => {
            setModalContent(() => () => (
              <AIDoctorIntakeFlow
                type={WorkflowType.rx_renewal}
                onClose={() => setModalContent(null)}
              />
            ));
          }}
          disabled={isPending}
          alignSelf="stretch"
          dataTrackingId="glp1-refill-existing-prescription"
        >
          Refill existing GLP-1 prescription
        </Button>
        <Paragraph textAlign="center" size="$xs" color="$textPrimary">
          By continuing, you agree to our{" "}
          <Link size="$xs" href="/terms" bold>
            Terms
          </Link>{" "}
          and{" "}
          <Link size="$xs" href="/privacy" bold>
            Privacy Policy
          </Link>
          .
        </Paragraph>
      </Section>
    </YStack>
  );

  const image = (
    <Square
      size={350}
      $xl={{ size: 400 }}
      borderRadius="$150"
      overflow="hidden"
      shadowRadius="$400"
      shadowOpacity={0.3}
      shadowColor="$shadowPartnerHero"
      shadowOffset={{ width: 0, height: 4 }}
      alignSelf="center"
    >
      <Image
        width="100%"
        height="100%"
        objectFit="cover"
        source={GlpHeroImage}
      />
    </Square>
  );

  if (lg) {
    return (
      <XStack
        maxWidth="$landingPageConstraint"
        alignSelf="center"
        paddingHorizontal="$250"
        gap="$800"
        justifyContent="center"
        alignItems="stretch"
        paddingVertical={140}
      >
        {textContent}
        {image}
      </XStack>
    );
  }

  return (
    <YStack
      maxWidth="$landingPageConstraint"
      alignSelf="center"
      paddingHorizontal="$250"
      paddingVertical="$800"
      gap="$600"
      width="100%"
    >
      {textContent}
      {image}
    </YStack>
  );
}
